import { useMutation } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import { usePageContext } from '../usePageContext.ts';
import { STOCK_RESOURCES, type CancelReason, type Order } from './orders.ts';

type OrderMove =
  | { readonly kind: 'confirm' | 'reopen'; readonly order: Order }
  | { readonly kind: 'cancel'; readonly order: Order; readonly reason: CancelReason }
  | {
      readonly kind: 'dispatch';
      readonly order: Order;
      readonly carrierCode: string;
      readonly shippingFee: string;
      readonly idempotencyKey: string;
    };

const DONE_MESSAGE = {
  confirm: 'orders.confirmed',
  reopen: 'orders.reopened',
  cancel: 'orders.canceled',
  dispatch: 'orders.dispatchRequested',
} as const;

export const useOrderActions = (api: TypedClient) => {
  const { t, notify, invalidate } = usePageContext();
  const send = (move: OrderMove) => {
    const path = { id: move.order.id };
    const version = move.order.version;
    if (move.kind === 'dispatch')
      return api.post('/admin/orders/{id}/dispatch', {
        path,
        body: { version, carrierCode: move.carrierCode, shippingFee: move.shippingFee },
        idempotencyKey: move.idempotencyKey,
      });
    if (move.kind === 'cancel')
      return api.post('/admin/orders/{id}/cancel', { path, body: { version, reason: move.reason } });
    if (move.kind === 'confirm') return api.post('/admin/orders/{id}/confirm', { path, body: { version } });
    return api.post('/admin/orders/{id}/reopen', { path, body: { version } });
  };
  return useMutation({
    mutationFn: send,
    onSuccess: async (order, move) => {
      notify(t(DONE_MESSAGE[move.kind], { number: order.number }));
      await invalidate(...(move.kind === 'cancel' ? STOCK_RESOURCES : ['orders']));
    },
  });
};

export type OrderActions = ReturnType<typeof useOrderActions>;
